import { useOktaAuth } from '@okta/okta-react';
import BodyContentLoader from 'components/ContentLoader';
import { systemUser, systemUserCreditAnalyst, systemUserProcessingStaff, systemUserProdSupport, systemUserNonProd } from 'config/okta.config';
import { isAuthorizedUser } from 'constants/app.constants';
import { setUserInfo } from 'containers/App/actions';
import { makeSelectUserInfo } from 'containers/App/selector';
import PropTypes from 'prop-types';
import React, { FC, lazy, Suspense, useEffect } from 'react';
import { connect } from 'react-redux';
import { Route, Switch } from 'react-router';
import { bindActionCreators, compose, Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import { CustomContainer } from 'styles/global.style';

const WorkQueue = lazy(() => import('containers/WorkQueue'));
const WorkList = lazy(() => import('containers/WorkList'));
const Help = lazy(() => import('containers/Help'));
const MyWorklist = lazy(() => import('containers/MyWorklist'));
const AppContainer = lazy(() => import('containers/App'));

const AppRouter: FC<any> = ({ dispatchUserInfo, userInfo }) => {
  const { authState, oktaAuth } = useOktaAuth();

  useEffect(() => {
    if (process.env.LOCAL === 'true') {
      let tempUserInfo = systemUser();
      const userRole = (process.env.USER_ROLE || '').toUpperCase();
      if (userRole === 'ABTOOLS_CREDIT_ANALYST') {
        tempUserInfo = systemUserCreditAnalyst();
      } else if (userRole === 'ABTOOLS_PROCESSING_STAFF') {
        tempUserInfo = systemUserProcessingStaff();
      } else if (userRole === 'ABTOOLS_PROD_SUPPORT') {
        tempUserInfo = systemUserProdSupport();
      } else if (userRole === 'ABTOOLS_NON_PROD') {
        tempUserInfo = systemUserNonProd();
      }
      dispatchUserInfo(tempUserInfo);
    } else if (!authState.isAuthenticated) {
      // When user isn't authenticated, forget any user info
      dispatchUserInfo(null);
    } else {
      oktaAuth
        .getUser()
        .then((info: any) => {
          const abtoolsAdGroups = info.abtoolsAdGroups.map((role: any) => role.toUpperCase());
          console.log('[AppRouter] abtoolsAdGroups at runtime:', abtoolsAdGroups); // eslint-disable-line no-console
          if (!isAuthorizedUser(abtoolsAdGroups)) {
            dispatchUserInfo({ ...info, abtoolsAdGroups, accessToken: authState.accessToken, unauthorized: true });
          } else {
            dispatchUserInfo({ ...info, abtoolsAdGroups, accessToken: authState.accessToken });
          }
        })
        .catch((err: any) => {
          console.log(err); // eslint-disable-line no-console
        });
    }
  }, [authState, oktaAuth]); // Update if authState changes
  return (
    <>
      {userInfo && userInfo.unauthorized && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column' }}>
          <h2>Access Denied</h2>
          <p>You do not have the required permissions to access this application.</p>
          <p>Please contact your administrator to request access.</p>
        </div>
      )}
      {userInfo && !userInfo.unauthorized && (
        <Suspense fallback={<BodyContentLoader />}>
          <AppContainer />
          <CustomContainer>
            <Switch>
              <Route exact component={WorkQueue} path="/" />
              <Route exact component={WorkList} path="/worklist" />
              <Route exact component={MyWorklist} path="/my-worklist" />
              <Route exact component={Help} path="/help" />
            </Switch>
          </CustomContainer>
        </Suspense>
      )}
    </>
  );
};

const mapStateToProps = createStructuredSelector<any, any>({
  userInfo: makeSelectUserInfo(),
});

const mapDispatchToProps = (dispatch: Dispatch<any>): any => bindActionCreators({ dispatchUserInfo: setUserInfo }, dispatch);

AppRouter.defaultProps = {
  userInfo: null,
  dispatchUserInfo: () => {},
};
AppRouter.propTypes = {
  userInfo: PropTypes.oneOfType([PropTypes.any]),
  dispatchUserInfo: PropTypes.oneOfType([PropTypes.any]),
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withConnect)(AppRouter);
